import { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  Tooltip,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Input,
} from '@fluentui/react-components';
import {
  BoxMultipleRegular,
  ClipboardTaskListLtrRegular,
  ListRegular,
  SettingsRegular,
  QuestionCircleRegular,
  MailRegular,
  BookQuestionMarkRegular,
  ChatHelpRegular,
  LinkRegular,
  MoneyCalculatorRegular,
  TaskListSquareLtrRegular,
  WrenchScrewdriverRegular,
  BoardRegular,
  BookDatabaseRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ZoomInRegular,
  ZoomOutRegular,
  PeopleSwapRegular,
  DismissRegular,
  SearchRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { useAuth, useIsAdmin } from '../../../providers/AuthProvider';
import { dataverseClient } from '../../../api';
import { escapeODataString, sanitizeUserSearchQuery } from '../../../api/dataverse/utils';
import { COLORS, dark, veracityColors, VeraMark, ModuleSwitcher, EnvBadge, UserMenu } from '../../../ui';
import { userActivityTracker } from '../../../entities/user-activity';

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  
  // Header
  header: {
    height: '48px',
    backgroundColor: COLORS.primary,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  veraMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: '4px',
  },
  waffleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  appTitle: {
    color: COLORS.white,
    fontSize: '16px',
    fontWeight: '600',
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  envBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '4px 16px',
    marginRight: '8px',
  },
  envLabel: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.7)',
  },
  envValue: {
    fontSize: '13px',
    color: COLORS.white,
    fontWeight: '500',
  },
  headerIconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px 4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: COLORS.white,
    cursor: 'pointer',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '500',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  // Main Layout
  mainLayout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  
  // Sidebar
  sidebar: {
    width: '220px',
    backgroundColor: dark.bgBase,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    transitionProperty: 'width',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  sidebarCollapsed: {
    width: '48px',
  },
  navGroup: {
    padding: '8px 0',
    borderBottom: `1px solid ${dark.border}`,
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    color: 'rgba(255,241,224,0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '0.1s',
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: dark.bgSurface,
    },
  },
  navItemCollapsed: {
    padding: '10px 0',
    justifyContent: 'center',
    gap: '0',
  },
  navItemSelected: {
    backgroundColor: dark.bgElevated,
    borderLeft: '3px solid var(--app-accent, #D6461D)',
    paddingLeft: '13px',
  },
  navItemSelectedCollapsed: {
    paddingLeft: '0',
    borderLeft: '3px solid var(--app-accent, #D6461D)',
  },
  navItemDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  navIcon: {
    fontSize: '20px',
    flexShrink: 0,
    opacity: 0.9,
  },
  navLabel: {
    flex: 1,
    overflow: 'hidden',
  },
  navLabelHidden: {
    display: 'none',
  },
  navBadge: {
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: dark.textMuted,
  },
  navSubItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px 6px 44px',
    color: dark.textMuted,
    fontSize: '12px',
    textDecoration: 'none',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
    },
  },
  navSubItemSelected: {
    color: '#fff',
    fontWeight: '600',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  collapseToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    borderTop: `1px solid rgba(255,255,255,0.15)`,
    ':hover': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  
  // Content
  content: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: COLORS.bgBase,
  },
  
  // Help Dialog
  helpSection: {
    marginBottom: '20px',
  },
  helpSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: '12px',
  },
  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: COLORS.textPrimary,
    textDecoration: 'none',
    ':hover': {
      backgroundColor: COLORS.bgBase,
    },
  },
  helpLinkIcon: {
    fontSize: '20px',
    color: COLORS.primary,
  },
  helpLinkText: {
    flex: 1,
  },
  helpLinkTitle: {
    fontSize: '14px',
    fontWeight: '500',
  },
  helpLinkDesc: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },
  
  // User menu header
  userMenuHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: '4px',
  },
  userMenuName: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
  },
  userMenuEmail: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    marginTop: '2px',
  },

  // App Launcher
  launcherPanel: {
    padding: '16px',
    width: '280px',
  },
  launcherTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: '12px',
  },
  launcherGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '4px',
  },
  launcherTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: COLORS.bgSurface,
    },
  },
  launcherTileCurrent: {
    backgroundColor: COLORS.bgSurface,
    boxShadow: `inset 0 0 0 1.5px ${veracityColors.veracityOrange}`,
  },
  launcherTileIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#FFFFFF',
  },
  launcherTileLabel: {
    fontSize: '11px',
    color: COLORS.textPrimary,
    textAlign: 'center',
    lineHeight: '1.2',
    fontWeight: '500',
  },

  // Impersonation Banner
  impersonationBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '6px 16px',
    backgroundColor: '#D6461D',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: '500',
    flexShrink: 0,
  },
  impersonationStopBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(255,255,255,0.35)',
    },
  },

  // Impersonate Dialog
  userSearchResults: {
    maxHeight: '300px',
    overflowY: 'auto',
    marginTop: '12px',
  },
  userSearchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: COLORS.bgBase,
    },
  },
  userSearchAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: COLORS.bgSurface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: COLORS.textSecondary,
    flexShrink: 0,
  },
  userSearchInfo: {
    flex: 1,
    minWidth: 0,
  },
  userSearchName: {
    fontSize: '14px',
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  userSearchEmail: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// =============================================================================
// App Launcher Config
// =============================================================================

export type VeraAppId = 'catalog' | 'ops' | 'sales';

interface VeraApp {
  id: VeraAppId;
  label: string;
  icon: typeof BoxMultipleRegular;
  color: string;
}

const VERA_APPS: VeraApp[] = [
  {
    id: 'sales',
    label: 'Vera Sales',
    icon: MoneyCalculatorRegular,
    color: '#553746',
  },
  {
    id: 'ops',
    label: 'Vera Ops',
    icon: TaskListSquareLtrRegular,
    color: COLORS.primary,
  },
  {
    id: 'catalog',
    label: 'Vera Catalog',
    icon: BookDatabaseRegular,
    color: '#3F4C5C',
  },
];

// =============================================================================
// Navigation Items
// =============================================================================

export interface NavItem {
  path: string;
  label: string;
  icon: typeof BoxMultipleRegular;
  disabled?: boolean;
  badge?: string;
  children?: { path: string; label: string }[];
}

const defaultNavItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: BoardRegular },
  { path: '/solutions', label: 'Solutions', icon: BoxMultipleRegular },
  { path: '/group-info', label: 'Group Info Template', icon: ClipboardTaskListLtrRegular },
  { path: '/implementation', label: 'Implementation', icon: WrenchScrewdriverRegular },
  { path: '/managed-lists', label: 'Managed Lists', icon: ListRegular },
  { path: '/partner-ids', label: 'Partner IDs', icon: LinkRegular },
  { path: '/demo-preview', label: 'Demo Preview', icon: EyeRegular },
  { path: '/admin-config', label: 'Configuration', icon: SettingsRegular },
];

// =============================================================================
// Helper Functions
// =============================================================================

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return (parts[0] ?? '').substring(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

// =============================================================================
// Component
// =============================================================================

interface AppShellProps {
  children: ReactNode;
  appId?: VeraAppId;
  appTitle?: string;
  navItems?: NavItem[];
  headerExtra?: ReactNode;
  /** Optional search widget rendered in the header, left of the env badge / headerExtra. */
  headerSearch?: ReactNode;
  headerColor?: string;
  /** Custom content for the settings dialog. If not provided, shows a placeholder. */
  settingsContent?: ReactNode;
}

export function AppShell({ children, appId = 'catalog', appTitle = 'Vera Catalog', navItems = defaultNavItems, headerExtra, headerSearch, headerColor: headerColorProp, settingsContent }: AppShellProps) {
  // Resolve header color: explicit prop wins, otherwise look up from VERA_APPS by appId
  const headerColor = headerColorProp ?? VERA_APPS.find((a) => a.id === appId)?.color;
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  // Auth
  const { email, impersonation, startImpersonation, stopImpersonation } = useAuth();
  const isAdmin = useIsAdmin();

  // State
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateSearch, setImpersonateSearch] = useState('');
  const [impersonateResults, setImpersonateResults] = useState<Array<{ oid: string; name: string; email: string }>>([]);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem(`vera-${appId}-sidebar-collapsed`) === 'true';
  });

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem(`vera-${appId}-sidebar-collapsed`, String(next));
  };

  // =====================================================================
  // User activity tracking — emit area_visit on nav changes.
  // setApp(...) is called at module load in each app's App.tsx so that the
  // login event fires even for users who get blocked by ProtectedRoute.
  // =====================================================================
  useEffect(() => {
    // Find the top-level nav item whose path matches the current location
    // and use its label as the "area" for telemetry.
    const path = location.pathname;
    const match = navItems.find((item) => {
      if (item.path === '/') return path === '/';
      if (path === item.path || path.startsWith(item.path + '/')) return true;
      return item.children?.some((c) => path === c.path || path.startsWith(c.path + '/')) ?? false;
    });
    const area = match?.label ?? 'Unknown';
    userActivityTracker.trackAreaVisit(area);
  }, [location.pathname, navItems]);

  // Zoom
  const ZOOM_STEPS = [0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2, 1.25];
  const ZOOM_DEFAULT = 1;
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('vera-zoom');
    return saved ? Number(saved) : ZOOM_DEFAULT;
  });
  const zoomIn = () => {
    const idx = ZOOM_STEPS.indexOf(zoom);
    const next = idx < ZOOM_STEPS.length - 1 ? ZOOM_STEPS[idx + 1] ?? zoom : zoom;
    setZoom(next);
    localStorage.setItem('vera-zoom', String(next));
  };
  const zoomOut = () => {
    const idx = ZOOM_STEPS.indexOf(zoom);
    const next = idx > 0 ? ZOOM_STEPS[idx - 1] ?? zoom : zoom;
    setZoom(next);
    localStorage.setItem('vera-zoom', String(next));
  };
  const resetZoom = () => {
    setZoom(ZOOM_DEFAULT);
    localStorage.setItem('vera-zoom', String(ZOOM_DEFAULT));
  };

  // Impersonation: search Dataverse system users
  const searchUsers = useCallback(async (query: string) => {
    const safe = sanitizeUserSearchQuery(query);
    if (safe.length < 2) {
      setImpersonateResults([]);
      return;
    }
    setImpersonateLoading(true);
    try {
      const result = await dataverseClient.list<{
        systemuserid: string;
        fullname: string;
        internalemailaddress: string;
        azureactivedirectoryobjectid: string;
        isdisabled: boolean;
      }>('systemusers', {
        filter: `(contains(fullname,'${escapeODataString(safe)}') or contains(internalemailaddress,'${escapeODataString(safe)}')) and isdisabled eq false and azureactivedirectoryobjectid ne null`,
        select: ['systemuserid', 'fullname', 'internalemailaddress', 'azureactivedirectoryobjectid'],
        top: 20,
        orderBy: 'fullname asc',
      });
      setImpersonateResults(
        result.value
          .filter((u) => u.azureactivedirectoryobjectid)
          .map((u) => ({
            oid: u.azureactivedirectoryobjectid,
            name: u.fullname || 'Unknown',
            email: u.internalemailaddress || '',
          }))
      );
    } catch (err) {
      console.error('User search failed:', err);
      setImpersonateResults([]);
    } finally {
      setImpersonateLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!impersonateDialogOpen) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchUsers(impersonateSearch), 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [impersonateSearch, impersonateDialogOpen, searchUsers]);

  const handlePickUser = async (target: { oid: string; name: string; email: string }) => {
    try {
      await startImpersonation(target);
      setImpersonateDialogOpen(false);
      setImpersonateSearch('');
      setImpersonateResults([]);
      window.location.reload(); // clean reload to reset all queries
    } catch (err) {
      console.error('Failed to start impersonation:', err);
    }
  };

  const handleStopImpersonation = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonation();
      window.location.reload();
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      setStoppingImpersonation(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className={styles.container} style={headerColor ? { '--app-accent': headerColor } as React.CSSProperties : undefined}>
      {/* Header */}
      <header className={styles.header} style={headerColor ? { backgroundColor: headerColor } : undefined}>
        <div className={styles.headerLeft}>
          <ModuleSwitcher onDark />
          <div className={styles.veraMark}>
            <VeraMark size={32} />
          </div>
          <span className={styles.appTitle}>{appTitle}</span>
        </div>

        <div className={styles.headerRight}>
          {headerSearch && <div style={{ marginRight: '8px' }}>{headerSearch}</div>}
          {headerExtra ?? <EnvBadge variant="on-dark" />}

          {/* Zoom controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: '4px' }}>
            <button
              className={styles.headerIconBtn}
              title="Zoom out"
              onClick={zoomOut}
              disabled={zoom <= (ZOOM_STEPS[0] ?? 0)}
            >
              <ZoomOutRegular style={{ fontSize: '18px' }} />
            </button>
            <button
              className={styles.headerIconBtn}
              title="Reset zoom"
              onClick={resetZoom}
              style={{ fontSize: '11px', minWidth: '36px', fontWeight: zoom !== ZOOM_DEFAULT ? 600 : 400 }}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              className={styles.headerIconBtn}
              title="Zoom in"
              onClick={zoomIn}
              disabled={zoom >= (ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? Infinity)}
            >
              <ZoomInRegular style={{ fontSize: '18px' }} />
            </button>
          </div>

          <Menu>
            <MenuTrigger>
              <button className={styles.headerIconBtn} title="Settings">
                <SettingsRegular style={{ fontSize: '20px' }} />
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setSettingsDialogOpen(true)}>
                  Application Settings
                </MenuItem>
                {isAdmin && !impersonation && (
                  <MenuItem icon={<PeopleSwapRegular />} onClick={() => setImpersonateDialogOpen(true)}>
                    View as Another User
                  </MenuItem>
                )}
                {impersonation && (
                  <MenuItem icon={<DismissRegular />} onClick={handleStopImpersonation}>
                    Stop Viewing as {impersonation.targetName}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>

          <button 
            className={styles.headerIconBtn} 
            title="Help"
            onClick={() => setHelpDialogOpen(true)}
          >
            <QuestionCircleRegular style={{ fontSize: '20px' }} />
          </button>

          <UserMenu onDark />
        </div>
      </header>

      {/* Impersonation Banner */}
      {impersonation && (
        <div className={styles.impersonationBanner}>
          <PeopleSwapRegular style={{ fontSize: '16px' }} />
          <span>
            Viewing as <strong>{impersonation.targetName}</strong> ({impersonation.targetEmail})
          </span>
          <button
            className={styles.impersonationStopBtn}
            onClick={handleStopImpersonation}
            disabled={stoppingImpersonation}
          >
            {stoppingImpersonation ? <Spinner size="extra-tiny" /> : <DismissRegular style={{ fontSize: '12px' }} />}
            Stop
          </button>
        </div>
      )}

      {/* Main Layout */}
      <div className={styles.mainLayout}>
        {/* Sidebar */}
        <nav className={mergeClasses(styles.sidebar, sidebarCollapsed && styles.sidebarCollapsed)}>
          <div className={styles.navGroup}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const childActive = item.children?.some((c) => isActive(c.path)) ?? false;
              const active = isActive(item.path) || childActive;

              if (item.disabled) {
                return (
                  <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                    <div
                      className={mergeClasses(
                        styles.navItem,
                        styles.navItemDisabled,
                        sidebarCollapsed && styles.navItemCollapsed,
                      )}
                    >
                      <Icon className={styles.navIcon} />
                      <span className={mergeClasses(styles.navLabel, sidebarCollapsed && styles.navLabelHidden)}>{item.label}</span>
                      {!sidebarCollapsed && item.badge && <span className={styles.navBadge}>{item.badge}</span>}
                    </div>
                  </Tooltip>
                );
              }

              const navLink = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={mergeClasses(
                    styles.navItem,
                    sidebarCollapsed && styles.navItemCollapsed,
                    active && (sidebarCollapsed ? styles.navItemSelectedCollapsed : styles.navItemSelected),
                  )}
                >
                  <Icon className={styles.navIcon} />
                  <span className={mergeClasses(styles.navLabel, sidebarCollapsed && styles.navLabelHidden)}>{item.label}</span>
                </NavLink>
              );

              if (sidebarCollapsed) {
                // Items with children: show flyout popover with child links
                if (item.children && item.children.length > 0) {
                  return (
                    <Popover key={item.path} positioning="after" withArrow openOnHover mouseLeaveDelay={200}>
                      <PopoverTrigger disableButtonEnhancement>
                        {navLink}
                      </PopoverTrigger>
                      <PopoverSurface style={{
                        padding: '8px 0', minWidth: '160px',
                        backgroundColor: dark.bgBase, border: `1px solid ${dark.bgElevated}`,
                        borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                      }}>
                        <div style={{ padding: '4px 12px 8px', fontSize: '11px', fontWeight: 600, color: dark.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {item.label}
                        </div>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            style={{
                              display: 'block', padding: '8px 16px', fontSize: '13px',
                              color: isActive(child.path) ? '#fff' : 'rgba(255,241,224,0.85)',
                              textDecoration: 'none', fontWeight: isActive(child.path) ? 600 : 400,
                              backgroundColor: isActive(child.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isActive(child.path) ? 'rgba(255,255,255,0.1)' : 'transparent'; }}
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </PopoverSurface>
                    </Popover>
                  );
                }
                // Items without children: simple tooltip
                return (
                  <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                    {navLink}
                  </Tooltip>
                );
              }

              if (item.children && item.children.length > 0) {
                const anyChildActive = item.children.some((c) => isActive(c.path));
                const showChildren = active || anyChildActive;
                return (
                  <div key={item.path}>
                    {navLink}
                    {showChildren && item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={mergeClasses(
                          styles.navSubItem,
                          isActive(child.path) && styles.navSubItemSelected,
                        )}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                );
              }

              return navLink;
            })}
          </div>
          <button
            className={styles.collapseToggle}
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={headerColor ? { backgroundColor: headerColor } : undefined}
          >
            {sidebarCollapsed ? <ChevronRightRegular /> : <ChevronLeftRegular />}
          </button>
        </nav>

        {/* Content */}
        <main className={styles.content} style={zoom !== 1 ? { zoom } : undefined}>{children}</main>
      </div>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onOpenChange={(_, d) => setHelpDialogOpen(d.open)}>
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>Help & Support</DialogTitle>
            <DialogContent>
              <div className={styles.helpSection}>
                <div className={styles.helpSectionTitle}>Documentation</div>
                <div
                  className={styles.helpLink}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { navigate('/guide'); setHelpDialogOpen(false); }}
                >
                  <BookQuestionMarkRegular className={styles.helpLinkIcon} />
                  <div className={styles.helpLinkText}>
                    <div className={styles.helpLinkTitle}>User Guide</div>
                    <div className={styles.helpLinkDesc}>Learn how to use this app</div>
                  </div>
                </div>
              </div>
              <div className={styles.helpSection}>
                <div className={styles.helpSectionTitle}>Contact Support</div>
                <a href="mailto:support@veracity-benefits.com" className={styles.helpLink}>
                  <MailRegular className={styles.helpLinkIcon} />
                  <div className={styles.helpLinkText}>
                    <div className={styles.helpLinkTitle}>Email Support</div>
                    <div className={styles.helpLinkDesc}>support@veracity-benefits.com</div>
                  </div>
                </a>
                <div className={styles.helpLink} style={{ cursor: 'default', opacity: 0.45 }}>
                  <ChatHelpRegular className={styles.helpLinkIcon} />
                  <div className={styles.helpLinkText}>
                    <div className={styles.helpLinkTitle}>Live Chat</div>
                    <div className={styles.helpLinkDesc}>Coming soon</div>
                  </div>
                </div>
              </div>
              <div style={{ 
                padding: '12px', 
                backgroundColor: COLORS.bgSurface, 
                borderRadius: '6px',
                fontSize: '12px',
                color: COLORS.textSecondary
              }}>
                <strong>Version:</strong> 1.0.0<br />
                <strong>Last Updated:</strong> {new Date().toLocaleDateString()}<br />
                <strong>Logged in as:</strong> {email ?? ''}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setHelpDialogOpen(false)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={(_, d) => setSettingsDialogOpen(d.open)}>
        <DialogSurface style={{ maxWidth: settingsContent ? '640px' : '500px' }}>
          <DialogBody>
            <DialogTitle>Application Settings</DialogTitle>
            <DialogContent>
              {settingsDialogOpen ? (settingsContent ?? (
                <p style={{ color: COLORS.textSecondary }}>
                  Application settings will be available in a future update.
                </p>
              )) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setSettingsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* Impersonate User Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={(_, d) => { setImpersonateDialogOpen(d.open); if (!d.open) { setImpersonateSearch(''); setImpersonateResults([]); } }}>
        <DialogSurface style={{ maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>View as Another User</DialogTitle>
            <DialogContent>
              <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '12px' }}>
                Search for a user to view the app from their perspective. Dataverse will apply their security roles.
              </p>
              <Input
                placeholder="Search by name or email..."
                value={impersonateSearch}
                onChange={(_, data) => setImpersonateSearch(data.value)}
                contentBefore={<SearchRegular />}
                style={{ width: '100%' }}
                autoFocus
              />
              <div className={styles.userSearchResults}>
                {impersonateLoading && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Spinner size="small" label="Searching..." />
                  </div>
                )}
                {!impersonateLoading && impersonateSearch.length >= 2 && impersonateResults.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px' }}>
                    No users found
                  </div>
                )}
                {!impersonateLoading && impersonateResults.map((u) => (
                  <div
                    key={u.oid}
                    className={styles.userSearchItem}
                    onClick={() => handlePickUser(u)}
                  >
                    <div className={styles.userSearchAvatar}>
                      {getInitials(u.name)}
                    </div>
                    <div className={styles.userSearchInfo}>
                      <div className={styles.userSearchName}>{u.name}</div>
                      <div className={styles.userSearchEmail}>{u.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImpersonateDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export default AppShell;
